import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Zoom threshold for showing preview icons
const PREVIEW_ZOOM_THRESHOLD = 8;

// Custom icons. Use L.Icon with a data-URI SVG so the marker is an <img>
// element — natively interactive and shows hand cursor on hover.
const ORIGINAL_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">' +
  '<circle cx="14" cy="14" r="10" fill="#22c55e" stroke="#ffffff" stroke-width="3"/>' +
  '</svg>'
);
const GUESS_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">' +
  '<circle cx="14" cy="14" r="10" fill="#ef4444" stroke="#ffffff" stroke-width="3"/>' +
  '</svg>'
);

const originalIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;charset=UTF-8,${ORIGINAL_SVG}`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
  className: 'aam-marker-wrap',
});

const guessIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;charset=UTF-8,${GUESS_SVG}`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
  className: 'aam-marker-wrap',
});

// Function to create preview icon
const createPreviewIcon = (previewUrl, isOriginal = true) => {
  const borderColor = isOriginal ? '#22c55e' : '#ef4444';
  return L.divIcon({
    html: `
      <div style="
        width: 80px;
        height: 60px;
        border: 3px solid ${borderColor};
        border-radius: 8px;
        overflow: hidden;
        background: white;
        box-shadow: 0 4px 8px rgba(0,0,0,0.3);
        cursor: pointer;
        transition: transform 0.2s, box-shadow 0.2s;
      "
      onmouseover="this.style.transform='scale(1.1)'; this.style.boxShadow='0 6px 12px rgba(0,0,0,0.4)'"
      onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 4px 8px rgba(0,0,0,0.3)'"
      >
        <img 
          src="${previewUrl}" 
          style="
            width: 100%;
            height: 100%;
            object-fit: cover;
            pointer-events: none;
          "
          onerror="this.style.display='none'"
        />
      </div>
    `,
    iconSize: [80, 60],
    iconAnchor: [40, 30],
    popupAnchor: [0, -30],
    className: 'preview-marker-icon'
  });
};

// Helper to format distance: < 1000m shown in meters, >= 1000m in km
const formatDistance = (distance) => {
  if (distance == null) return '?';
  if (distance < 1000) return `${Math.round(distance)} м`;
  return `${(distance / 1000).toFixed(2)} км`;
};

// Function to create guess marker with score and distance (before winners)
const createGuessMarkerWithInfo = (score, distance, showPreview, previewUrl) => {
  const distanceText = formatDistance(distance);
  
  if (showPreview && previewUrl) {
    return L.divIcon({
      html: `
        <div style="display: flex; flex-direction: column; align-items: center;">
          <div style="
            width: 80px;
            height: 60px;
            border: 3px solid #ef4444;
            border-radius: 8px;
            overflow: hidden;
            background: white;
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
            margin-bottom: 4px;
          ">
            <img 
              src="${previewUrl}" 
              style="width: 100%; height: 100%; object-fit: cover;"
              onerror="this.style.display='none'"
            />
          </div>
          <div style="
            background: white;
            padding: 4px 8px;
            border-radius: 4px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            font-size: 12px;
            font-weight: bold;
            color: #2d3748;
            white-space: nowrap;
          ">
            ${score} очков<br/>${distanceText}
          </div>
        </div>
      `,
      iconSize: [80, 90],
      iconAnchor: [40, 90],
      popupAnchor: [0, -90],
      className: 'guess-marker-with-info'
    });
  }
  
  // Simple marker with info
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #ef4444;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          margin-bottom: 4px;
        "></div>
        <div style="
          background: white;
          padding: 4px 8px;
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          font-size: 12px;
          font-weight: bold;
          color: #2d3748;
          white-space: nowrap;
        ">
          ${score} очков<br/>${distanceText}
        </div>
      </div>
    `,
    iconSize: [80, 70],
    iconAnchor: [40, 70],
    popupAnchor: [0, -70],
    className: 'guess-marker-with-info'
  });
};

// Component to track zoom level
const ZoomTracker = ({ setZoomLevel }) => {
  const map = useMap();
  
  useEffect(() => {
    const updateZoom = () => {
      setZoomLevel(map.getZoom());
    };
    
    map.on('zoomend', updateZoom);
    updateZoom(); // Initial zoom
    
    return () => {
      map.off('zoomend', updateZoom);
    };
  }, [map, setZoomLevel]);
  
  return null;
};

// Component to handle zoom to answer
const ZoomToAnswer = ({ targetBounds }) => {
  const map = useMap();
  
  useEffect(() => {
    if (targetBounds) {
      map.fitBounds(targetBounds, {
        padding: [100, 100],
        maxZoom: 15,
        animate: true,
        duration: 0.8
      });
    }
  }, [targetBounds, map]);
  
  return null;
};

// Component to fit map to all answer points on initial load
const FitAllBounds = ({ bounds }) => {
  const map = useMap();
  const fittedRef = React.useRef(false);

  useEffect(() => {
    if (!fittedRef.current && bounds && bounds.length > 0) {
      map.fitBounds(bounds, {
        padding: [10, 10],
        animate: false
      });
      fittedRef.current = true;
    }
  }, [bounds, map]);

  return null;
};

const AllAnswersMap = ({ isOpen, onClose, gameId, myResults, panoramas, winnersComplete = false }) => {
  const [mapKey, setMapKey] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(4);
  const [infoCard, setInfoCard] = useState(null); // { panoId, kind, lat, lng, score, distance, previewUrl }
  const [cardPos, setCardPos] = useState(null);   // { x, y } - marker's container-pixel position
  const mapRef = React.useRef(null);

  useEffect(() => {
    if (isOpen) {
      setMapKey(prev => prev + 1);
      setZoomLevel(4);
      setInfoCard(null);
      setCardPos(null);
    }
  }, [isOpen]);

  // Click on a marker: open card. On mobile additionally pan map so the
  // marker is centered horizontally and the popup fits above it.
  const handleMarkerClick = (data) => {
    setInfoCard(data);
    if (mapRef.current && window.innerWidth <= 640) {
      mapRef.current.panTo([data.lat, data.lng], { animate: true });
    }
  };

  // Track the marker's pixel position in the map container so the card can be
  // anchored directly above it; recompute on pan/zoom so the card follows.
  useEffect(() => {
    if (!infoCard || !mapRef.current) {
      setCardPos(null);
      return;
    }
    const map = mapRef.current;
    const update = () => {
      const p = map.latLngToContainerPoint([infoCard.lat, infoCard.lng]);
      setCardPos({ x: p.x, y: p.y });
    };
    update();
    map.on('move zoom viewreset', update);
    return () => { map.off('move zoom viewreset', update); };
  }, [infoCard]);

  // Close the info card on any click that is NOT inside the card itself
  // and NOT on a marker (markers update the card content).
  useEffect(() => {
    if (!infoCard) return;
    const onDocClick = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('.all-answers-info-card')) return;       // clicks on card → keep open
      if (t.closest('.aam-marker-wrap')) return;             // clicks on markers handled separately
      setInfoCard(null);
    };
    const id = window.setTimeout(() => {
      document.addEventListener('click', onDocClick);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('click', onDocClick);
    };
  }, [infoCard]);

  if (!isOpen) return null;

  // Filter answered panoramas
  const answeredPanoramas = panoramas.filter(pano => {
    const answer = myResults?.answers?.find(a => a.panorama_id === pano.id);
    return answer && answer.guess_lat && answer.guess_lng;
  });

  if (answeredPanoramas.length === 0) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '8px',
          maxWidth: '400px'
        }}>
          <h2 style={{ marginBottom: '1rem' }}>Нет ответов</h2>
          <p>Вы еще не ответили ни на одну панораму в этой игре.</p>
          <Button onClick={onClose} style={{ marginTop: '1rem' }}>
            Закрыть
          </Button>
        </div>
      </div>
    );
  }

  // Calculate center and bounds
  const allLats = [];
  const allLngs = [];
  
  answeredPanoramas.forEach(pano => {
    const answer = myResults.answers.find(a => a.panorama_id === pano.id);
    // Always include guess; include original only after winners complete
    allLats.push(answer.guess_lat);
    allLngs.push(answer.guess_lng);
    if (winnersComplete) {
      allLats.push(pano.latitude);
      allLngs.push(pano.longitude);
    }
  });

  const centerLat = (Math.min(...allLats) + Math.max(...allLats)) / 2;
  const centerLng = (Math.min(...allLngs) + Math.max(...allLngs)) / 2;

  // Bounds for initial fit-to-all
  const allBounds = allLats.map((lat, i) => [lat, allLngs[i]]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <style>{`
        /* Lift marker pane ABOVE popup pane so clicks reach markers.
           (Default Leaflet: markerPane=600, popupPane=700 — but popup pane
           is full-viewport and absorbs all clicks since it's later in DOM.) */
        .leaflet-marker-pane {
          z-index: 800 !important;
        }
        .leaflet-marker-pane .leaflet-marker-icon,
        .leaflet-marker-pane .aam-marker-wrap {
          pointer-events: auto !important;
          cursor: pointer !important;
        }

        .all-answers-map-stats {
          position: absolute;
          bottom: 20px;
          left: 20px;
          background: white;
          padding: 1rem;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          z-index: 1000;
          max-width: 280px;
        }
        @media (max-width: 640px) {
          .all-answers-map-stats {
            left: 20px;
            right: 20px;
            max-width: none;
          }
        }
        /* Custom info card (replaces Leaflet Popup).
           Anchored at the marker's pixel position via inline left/top from JS.
           translate(-50%, calc(-100% - 18px)) puts the bottom-center of the card
           18px above the marker. Card moves with the map on pan/zoom. */
        .all-answers-info-card {
          position: absolute;
          width: 320px;
          max-width: calc(100vw - 20px);
          transform: translate(-50%, calc(-100% - 18px));
          background: white;
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.35);
          z-index: 1100;
          overflow: hidden;
          pointer-events: auto;
          animation: aamc-fade 0.18s ease-out;
        }
        /* Triangle pointer at the bottom of the card pointing to the marker */
        .all-answers-info-card::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 9px solid transparent;
          border-right: 9px solid transparent;
          border-top: 9px solid #ffffff;
          filter: drop-shadow(0 2px 2px rgba(0,0,0,0.15));
        }
        @keyframes aamc-fade {
          from { opacity: 0; transform: translate(-50%, calc(-100% - 26px)); }
          to   { opacity: 1; transform: translate(-50%, calc(-100% - 18px)); }
        }
        .all-answers-info-card-img {
          display: block;
          width: 100%;
          aspect-ratio: 2 / 1;
          object-fit: cover;
        }
        .all-answers-info-card-body {
          padding: 12px 14px 14px;
        }
        .all-answers-info-card-title {
          font-weight: 600;
          font-size: 14px;
          color: #1a202c;
          margin-bottom: 6px;
        }
        .all-answers-info-card-row {
          font-size: 13px;
          color: #4a5568;
          line-height: 1.5;
        }
      `}</style>
      {/* Map - full height */}
      <div style={{ flex: 1, position: 'relative' }}>
        {/* Floating close button - top right */}
        <Button
          variant="outline"
          onClick={onClose}
          data-testid="all-answers-map-close-btn"
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 1100,
            background: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
        >
          <X size={20} />
        </Button>

        <MapContainer
          key={mapKey}
          ref={mapRef}
          center={[centerLat, centerLng]}
          zoom={4}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />

          <ZoomTracker setZoomLevel={setZoomLevel} />
          <FitAllBounds bounds={allBounds} />
          
          {answeredPanoramas.map(pano => {
            const answer = myResults.answers.find(a => a.panorama_id === pano.id);
            const previewUrl = pano.preview_path ? `${API}/panoramas/${pano.id}/preview` : null;
            
            if (winnersComplete) {
              // AFTER WINNERS COMPLETE: Show both markers and lines
              return (
                <React.Fragment key={pano.id}>
                  {/* Original location marker */}
                  <Marker
                    position={[pano.latitude, pano.longitude]}
                    icon={originalIcon}
                    eventHandlers={{
                      click: () => handleMarkerClick({
                        panoId: pano.id,
                        kind: 'original',
                        lat: pano.latitude,
                        lng: pano.longitude,
                        score: answer.score,
                        distance: answer.distance,
                        previewUrl,
                      }),
                    }}
                  />

                  {/* User's guess marker */}
                  <Marker
                    position={[answer.guess_lat, answer.guess_lng]}
                    icon={guessIcon}
                    eventHandlers={{
                      click: () => handleMarkerClick({
                        panoId: pano.id,
                        kind: 'guess',
                        lat: answer.guess_lat,
                        lng: answer.guess_lng,
                        score: answer.score,
                        distance: answer.distance,
                        previewUrl,
                      }),
                    }}
                  />

                  {/* Dashed line between original and guess */}
                  <Polyline
                    positions={[
                      [pano.latitude, pano.longitude],
                      [answer.guess_lat, answer.guess_lng]
                    ]}
                    color="#0a4797"
                    weight={2}
                    dashArray="5, 10"
                  />
                </React.Fragment>
              );
            } else {
              // BEFORE WINNERS: Show only guess markers
              return (
                <Marker
                  key={pano.id}
                  position={[answer.guess_lat, answer.guess_lng]}
                  icon={guessIcon}
                  eventHandlers={{
                    click: () => handleMarkerClick({
                      panoId: pano.id,
                      kind: 'guess',
                      lat: answer.guess_lat,
                      lng: answer.guess_lng,
                      score: answer.score,
                      distance: answer.distance,
                      previewUrl,
                    }),
                  }}
                />
              );
            }
          })}
        </MapContainer>

        {/* Custom React info card — anchored above the clicked marker.
            Closes via document-click handler when user taps anywhere outside it. */}
        {infoCard && cardPos && (
          <div
            className="all-answers-info-card"
            data-testid="all-answers-info-card"
            style={{ left: cardPos.x, top: cardPos.y }}
          >
            {infoCard.previewUrl && (
              <img
                src={infoCard.previewUrl}
                alt=""
                className="all-answers-info-card-img"
              />
            )}
            <div className="all-answers-info-card-body">
              <div className="all-answers-info-card-title">
                {infoCard.kind === 'original' ? 'Оригинальное местоположение' : 'Ваш ответ'}
              </div>
              <div className="all-answers-info-card-row">
                Очки: <b>{infoCard.score}</b>
              </div>
              <div className="all-answers-info-card-row">
                Расстояние: <b>{infoCard.distance != null ? formatDistance(infoCard.distance) : 'N/A'}</b>
              </div>
            </div>
          </div>
        )}

        {/* Stats - bottom left */}
        <div
          className="all-answers-map-stats"
          data-testid="all-answers-map-stats"
        >
          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Статистика:</div>
          <div style={{ fontSize: '14px' }}>
            Отвечено: {answeredPanoramas.length} из {panoramas.length}
          </div>
          <div style={{ fontSize: '14px' }}>
            Всего очков: {myResults.total_score}
          </div>
          {!winnersComplete && (
            <div
              data-testid="all-answers-map-pending-winners-msg"
              style={{
                fontSize: '12px',
                color: '#718096',
                marginTop: '0.75rem',
                paddingTop: '0.5rem',
                borderTop: '1px solid #e2e8f0',
                fontStyle: 'italic',
                lineHeight: 1.4
              }}
            >
              Оригинальные точки будут показаны после определения всех победителей
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllAnswersMap;
